import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { useQuery } from "react-query";
import ShipIcon from "../Media/livevessel.png";
import VoyageFlag from "../Media/voyageStartFlag.png";
import Waypoints from "../Media/waypoint.png";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { RootState } from "index";
import {
  getVesselsAction,
  setOnboardedVessels,
  setVesselID,
  setVoyageID,
} from "Store/Generic/ReportingSlice";
import { queryKeyes } from "shared/queryKeys";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { Col, Container, Row } from "reactstrap";
import ESLogo from "../Media/Logo_Horizontal.png";
import { Link } from "react-router-dom";
import apiGlobal from "global/api.global";

/* ------------------------------
   MAP PROVIDER CONFIG - MAPPLS (MapmyIndia)
   India-compliant map tiles
-------------------------------- */
const MAPPLS_API_KEY = process.env.REACT_APP_MAPPLS_API_KEY || "0ea918c36c5d42bcb0b6a4060b0cabd0";
const TILE_URL = `https://apis.mappls.com/advancedmaps/v1/${MAPPLS_API_KEY}/still_map/{z}/{x}/{y}.png`;
const TILE_ATTRIBUTION = '&copy; <a href="https://www.mappls.com/">Mappls</a> | &copy; <a href="https://about.mappls.com/api/terms-conditions/">Terms</a>';

// Track tile errors - if too many, key may be expired
let tileErrorCount = 0;
const handleTileError = () => {
  tileErrorCount++;
  if (tileErrorCount === 5) {
    console.error("⚠️ MAPPLS API KEY MAY BE EXPIRED - Multiple tile load failures detected!");
    console.error("Please check: https://apis.mappls.com/console/");
  }
};

/* ------------------------------
   LEAFLET FIX
-------------------------------- */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ------------------------------
   MAP HELPERS
-------------------------------- */
const ResizeHandler: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [map]);
  return null;
};

const FitBounds: React.FC<{ points: [number, number][] }> = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(L.latLngBounds(points).pad(0.12));
  }, [points, map]);
  return null;
};
/* ------------------------------
   CONVERT DEG/MIN/SEC
-------------------------------- */
const convertToDecimal = (
  deg: number | null | undefined,
  min: number | null | undefined,
  sec: number | null | undefined,
  dir: string | null | undefined
) => {
  if (deg == null || min == null || sec == null || dir == null || dir === "") return null;
  let decimal = Number(deg) + Number(min) / 60 + Number(sec) / 3600;
  if (dir.toLowerCase() === "south" || dir.toLowerCase() === "west") decimal = decimal * -1;
  return Number(decimal.toFixed(6));
};
/* ------------------------------
   MAIN COMPONENT
-------------------------------- */
const VesselMap: React.FC = () => {
  const dispatch = useDispatch();
  const { Vessels, VesselID, VoyageID, onboardedVessels } =
    useSelector((state: RootState) => state.Reporting);

  const NAV_API_URL = "https://v1.navapi.pro/aisp/svsl/HistoricalTracks";
  const NAV_API_TOKEN = "c059ce6db17faa6fab314279b5725bdc1766054737617";

  /* ------------------------------
     FETCH AIS TRACKS
  -------------------------------- */
  const fetchAIS = async (payload: {
    ImoNumber: string;
    HistoryFrom: string;
    HistoryUntil: string;
  }) => {
    const res = await apiGlobal.post(NAV_API_URL, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${NAV_API_TOKEN}`,
      },
    });
    return res.data;
  };

  /* ------------------------------
     INIT DATA
  -------------------------------- */
  useEffect(() => {
    dispatch(getVesselsAction("vessel_master"));
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(Vessels)) {
      dispatch(
        setOnboardedVessels(
          Vessels.filter((v: any) => v.onboarding_status === "onboarded")
        )
      );
    }
  }, [Vessels, dispatch]);

  const { data: reports } = useQuery(
    [queryKeyes.vessel.vesselVoyageReporting.key, VesselID, VoyageID],
    async () =>
      await Get(
        queryKeyes.vessel.vesselVoyageReporting.url(VesselID, VoyageID),
        VesselID,
        VoyageID
      ),
    { enabled: !!VesselID && !!VoyageID, staleTime: Infinity }
  );
  const reportPoints = useMemo<[number, number][]>(() => {
    if (Array.isArray(reports) && reports.length > 0) {
      return reports
        .map((r: any) => {
          const lat = convertToDecimal(
            r.position_lat_deg,
            r.position_lat_min,
            r.position_lat_sec,
            r.lat_direction
          );
          const lon = convertToDecimal(
            r.position_long_deg,
            r.position_long_min,
            r.position_long_sec,
            r.long_direction
          );
          if (lat == null || lon == null) return null;
          return [lat, lon] as [number, number];
        })
        .filter(Boolean) as [number, number][];
    }
    return []; // ❗ No fallback
  }, [reports]);

  const vessel =
    onboardedVessels.find((v: any) => v.id === VesselID) || null;

  /* ------------------------------
     VOYAGES
  -------------------------------- */
  const { data: voyages, isLoading: voyagesLoading } = useQuery(
    [queryKeyes.vessel.VoyageList.key, VesselID],
    () => Get(queryKeyes.vessel.VoyageList.url(VesselID), VesselID),
    { enabled: !!VesselID }
  );

  const selectedVoyage = useMemo(
    () => voyages?.find((v: any) => v.id === VoyageID),
    [voyages, VoyageID]
  );

  /* ------------------------------
     AIS QUERY (SAME PATTERN AS REPORTS)
  -------------------------------- */
  const { data: aisData } = useQuery(
    ["ais-tracks", vessel?.IMO_number, VoyageID],
    () =>
      fetchAIS({
        ImoNumber: vessel!.IMO_number.toString(),
        HistoryFrom: selectedVoyage!.departure_date_time,
        HistoryUntil: selectedVoyage!.voyage_end_date_time,
      }),
    {
      enabled: !!vessel && !!selectedVoyage,
      staleTime: Infinity,
    }
  );

  /* ------------------------------
     MAP POINTS
  -------------------------------- */
  const trackPoints = useMemo<[number, number][]>(() => {
    if (!Array.isArray(aisData)) return [];
    return aisData
      .map((p: any) =>
        p.lat != null && p.lon != null ? [p.lat, p.lon] : null
      )
      .filter(Boolean) as [number, number][];
  }, [aisData]);

  const [showLegend, setShowLegend] = useState(true);

  // Tile URL and attribution
  const tileUrl = TILE_URL;
  const attribution = TILE_ATTRIBUTION;

  // Icon for report waypoints
  const waypointIcon = L.icon({
    iconUrl: Waypoints,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

  const initialCenter: [number, number] =
    trackPoints.length > 0 ? trackPoints[0] : [20, 0];

  /* ------------------------------
     RENDER
  -------------------------------- */
  return (
    <div className="vessel-map-page px-0">
      <Container fluid className="px-0">
        {/* ================= HEADER ================= */}
        <Row className="align-items-center px-3 py-2 border-bottom bg-white map-header">
          {/* LEFT: Brand */}
          <Col md="auto" className="d-flex align-items-center pe-4 border-end">
            <Link to="/dashboard" className="d-flex align-items-center">
              <img src={ESLogo} alt="ecoSAIL" height={44} />
            </Link>
          </Col>

          {/* CENTER: Selectors */}
          <Col>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <label className="form-label small mb-1">
                  Vessel
                </label>
                <Select
                  options={onboardedVessels || []}
                  getOptionLabel={(o: any) => o.vessel_name}
                  getOptionValue={(o: any) => o.id}
                  value={vessel}
                  onChange={(o: any) => {
                    dispatch(setVesselID(o?.id ?? null));
                    dispatch(setVoyageID(null));
                  }}
                  isClearable
                  placeholder="Select vessel..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Col>

              <Col md={4}>
                <label className="form-label small mb-1">
                  Voyage
                </label>
                <Select
                  options={
                    voyages?.filter(
                      (x: any) =>
                        x.display && [2, 3].includes(x.voyage_status)
                    ) || []
                  }
                  getOptionLabel={(o: any) => o.voyage_number}
                  getOptionValue={(o: any) => o.id}
                  onChange={(o: any) =>
                    dispatch(setVoyageID(o?.id ?? null))
                  }
                  isLoading={voyagesLoading}
                  isDisabled={!vessel}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
              </Col>
            </Row>
          </Col>

          {/* RIGHT: Actions */}
          <Col
            md="auto"
            className="d-flex align-items-center gap-2 ps-4 border-start"
          >
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowLegend((s) => !s)}
            >
              {showLegend ? "Hide Legend" : "Show Legend"}
            </button>

            <button
              className="btn btn-sm btn-light border"
              onClick={() => window.close()}
            >
              Close
            </button>
          </Col>
        </Row>

        {/* ================= MAP ================= */}
        <div className="map-wrapper rounded shadow-sm overflow-hidden">
          <div className="map-legend">
            {showLegend && (
              <div className="map-legend card p-2 shadow-sm">
                <div className="legend-title">Map Legend</div>
                <div className="legend-item">
                  <img src={ShipIcon} /> <span>Active Vessel</span>
                </div>
                <div className="legend-item">
                  <img src={VoyageFlag} /> <span>Start Point</span>
                </div>
                <div className="legend-item">
                  <img src={VoyageFlag} /> <span>End Point</span>
                </div>
                <div className="legend-item">
                  <img src={Waypoints} /> <span>Waypoints</span>
                </div>
                <div className="legend-item">
                  <span className="legend-line" /> <span>Route Path</span>
                </div>
              </div>
            )}
          </div>

          <MapContainer
            center={initialCenter}
            zoom={3}
            minZoom={2}
            style={{ height: "100%", width: "100%" }}
          >
            <ResizeHandler />
            <TileLayer
              url={tileUrl}
              attribution={attribution}
              maxZoom={18}
              eventHandlers={{
                tileerror: () => handleTileError()
              }}
            />
            <FitBounds points={trackPoints} />
            <Polyline
              positions={trackPoints}
              pathOptions={{ color: "#00d4ff", weight: 5, opacity: 0.8 }}
            />

            {/* Report waypoints as markers (filter out invalid [0,0] positions) */}
            {Array.isArray(reportPoints) && reportPoints
              .filter(
                (p) =>
                  p &&
                  Number.isFinite(p[0]) &&
                  Number.isFinite(p[1]) &&
                  !(p[0] === 0 && p[1] === 0)
              )
              .map((pos, idx) => (
                <Marker key={`wp-${idx}`} position={pos as [number, number]} icon={waypointIcon} />
              ))}
          </MapContainer>
        </div>
      </Container>
    </div>
  );
};

export default VesselMap;